import React, { useEffect, useMemo, useState } from "react";

const SEPARATORI_RAPIDI = [
  { etichetta: "Spazio", valore: " " },
  { etichetta: "Virgola", valore: "," },
  { etichetta: "Trattino", valore: "-" },
  { etichetta: "Slash", valore: "/" },
  { etichetta: "Punto", valore: "." },
  // Solo per colonne PDF con più righe di testo impilate nella stessa cella
  // (es. una distinta base con codice/colore/descrizione/note tutti scritti
  // uno sotto l'altro in un'unica cella): il motore (routes/pdfEstrattore.js,
  // unisciPezziCella) le separa con un vero a capo, così ogni riga fisica
  // diventa una pillola a sé invece di finire tutto incollato con spazi.
  { etichetta: "A capo", valore: "\n" },
];

function dividi(testo, separatore) {
  if (!separatore) return [testo];
  if (separatore !== "\n") testo = testo.replace(/\n/g, " ");
  return testo.split(separatore).map((p) => p.trim()).filter((p) => p !== "");
}

// Risultato dell'estrazione a separatore su un testo (anche per intervalli
// "indice"→"indiceA"), solo per l'anteprima nel modal: la logica vera è in
// estraiValore (routes/api.js, routes/ordini.js, Mapping.jsx, ecc.).
function anteprimaEstrazione(testo, estr) {
  const parti = dividi(String(testo ?? "").trim(), estr.separatore);
  if (parti.length === 0) return "";
  if (Number.isFinite(Number(estr.minParti)) && parti.length < Number(estr.minParti)) return "";
  const i = estr.indice < 0 ? parti.length + estr.indice : estr.indice;
  if (estr.indiceA == null) return parti[i] ?? "";
  if (i < 0 || i >= parti.length) return "";
  const fine = estr.indiceA < 0 ? parti.length + estr.indiceA : estr.indiceA;
  return parti.slice(i, Math.min(fine, parti.length - 1) + 1).join(estr.separatore).trim();
}

// Da indice fisico (0-based dall'inizio) a "posizione fisica" salvata in
// estrazione.da/estrazione.a per la modalità "posizione" (stessa convenzione
// negativa-da-fondo della modalità a separatore: -1 = ultimo carattere).
function fisicaAEtichetta(indiceFisico, lunghezza, direzione) {
  return direzione === "fine" ? -(lunghezza - indiceFisico) : indiceFisico;
}

// Modalità "regola di testo": la parte che serve sta TRA due testi fissi (es. tra
// "> " e " - " nel titolo di una distinta Dior), qualunque sia il resto del
// testo. Si compila in un'espressione regolare (la stessa che legge estraiValore
// in routes/api.js, routes/ordini.js, Mapping.jsx, ecc., modalità "regex"): il
// primo "da" che arriva a "a" senza altri "da" in mezzo, cioè il più vicino.
const escapeRegex = (t) => String(t).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// "da" e "a" sono elenchi di alternative (es. "> " oppure "| "): vale la prima che
// si trova. Con "conTesti" i due testi trovati diventano il gruppo 1 e 3 (il
// risultato è il 2), solo per mostrare quale alternativa ha funzionato.
const aLista = (x) => (Array.isArray(x) ? x : [x ?? ""]).filter((t) => t !== "");
function patternTra(da, a, conTesti = false) {
  const daAlt = aLista(da).map(escapeRegex).join("|");
  const aAlt = aLista(a).map(escapeRegex).join("|");
  const dentro = daAlt ? `(?:(?!${daAlt})[\\s\\S])*?` : "[\\s\\S]*?";
  if (conTesti) return `(${daAlt || "^"})(${dentro})(${aAlt || "$"})`;
  return `${daAlt ? `(?:${daAlt})` : "^"}(${dentro})${aAlt ? `(?:${aAlt})` : "$"}`;
}
// Quali testi di contorno ha usato la regola su questo valore (null se non trova).
function testiTrovati(testo, da, a) {
  try {
    const m = new RegExp(patternTra(da, a, true)).exec(String(testo ?? ""));
    return m ? { da: m[1], a: m[3] } : null;
  } catch (e) {
    return null;
  }
}
// Stessa logica del ramo "regex" di estraiValore; null = regola non valida.
function anteprimaRegex(testo, pattern, gruppo = 1) {
  try {
    const m = new RegExp(pattern || "").exec(String(testo ?? ""));
    return m ? String(m[gruppo] ?? "").trim() : "";
  } catch (e) {
    return null;
  }
}
// Spazi resi visibili ("·") perché in questa modalità contano.
const visibile = (t) => String(t).replace(/ /g, "·");

const stileBtnAttivo ={ backgroundColor: "#1e3a8a", color: "#fff", borderColor: "#1e3a8a" };

// Piccolo strumento visuale: mostra il valore VERO di una colonna del file
// cliente diviso in "pillole" cliccabili (parole, se c'è un separatore, o
// singoli caratteri per codici compatti tipo "FVVE54619536"), così un
// operatore qualunque capisce a colpo d'occhio cosa sta scegliendo, senza
// dover ragionare in numeri astratti di posizione/indice.
export default function EstrazioneModal({
  nomeColonnaSorgente, nomeColonnaDestinazione, esempi, estrazioneIniziale, onConferma, onAnnulla,
}) {
  const [indiceEsempio, setIndiceEsempio] = useState(0);
  useEffect(() => { setTestoScelto(null); }, [indiceEsempio]);
  const [modalita, setModalita] = useState(
    estrazioneIniziale?.modalita === "posizione" ? "posizione"
      : estrazioneIniziale?.modalita === "regex" ? "regex" : "separatore"
  );
  // true se l'estrazione iniziale è a separatore (né "posizione" né "regex"):
  // solo allora indice/indiceA/separatore hanno un significato.
  const inizialeASeparatore = !!estrazioneIniziale
    && estrazioneIniziale.modalita !== "posizione" && estrazioneIniziale.modalita !== "regex";

  // --- Modalità "regola di testo" (la parte sta tra due testi fissi) ---
  const [testoScelto, setTestoScelto] = useState(null); // la parte selezionata col mouse (per controllare che la regola dia proprio quella)
  // Elenchi di alternative (di solito una sola): vedi patternTra.
  const [traDa, setTraDa] = useState(() => { const l = [].concat(estrazioneIniziale?.tra?.da ?? ""); return l.length ? l : [""]; });
  const [traA, setTraA] = useState(() => { const l = [].concat(estrazioneIniziale?.tra?.a ?? ""); return l.length ? l : [""]; });
  // Regola scritta a mano (avanzato): si parte da questa se la regola salvata
  // non è stata costruita con i due testi (es. creata a mano nel modello).
  const [regolaManuale, setRegolaManuale] = useState(
    estrazioneIniziale?.modalita === "regex" && !estrazioneIniziale.tra
  );
  const [patternManuale, setPatternManuale] = useState(
    estrazioneIniziale?.modalita === "regex" ? (estrazioneIniziale.pattern ?? "") : ""
  );

  // --- Modalità "separatore" (parole/pezzi divisi da un carattere) ---
  const [separatore, setSeparatore] = useState(estrazioneIniziale?.separatore ?? " ");
  const [separatorePersonalizzato, setSeparatorePersonalizzato] = useState(
    SEPARATORI_RAPIDI.some((s) => s.valore === (estrazioneIniziale?.separatore ?? " ")) ? "" : (estrazioneIniziale?.separatore ?? "")
  );
  const [indiceScelto, setIndiceScelto] = useState(
    inizialeASeparatore ? Number(estrazioneIniziale.indice) : null
  );
  // Intervallo di pillole (opzionale): "indiceScelto" è la prima, questa è
  // l'ultima (stessa convenzione di numerazione, negativa = dalla fine). Serve
  // quando la parte che interessa è fatta da PIÙ pillole di fila — es. un
  // colore "000 Blanc" (codice + nome) dentro "UCD60030011 000 Blanc T34".
  const [indiceSceltoA, setIndiceSceltoA] = useState(
    inizialeASeparatore && estrazioneIniziale.indiceA != null
      ? Number(estrazioneIniziale.indiceA) : null
  );
  const [modoIntervallo, setModoIntervallo] = useState(
    inizialeASeparatore && estrazioneIniziale.indiceA != null
  );
  // Soglia minima di pezzi (opzionale): per quando un pezzo IN MEZZO (non il
  // primo né l'ultimo) a volte manca del tutto invece di essere solo più
  // corto — es. "codice_variante_colore" dove "variante" a volte non c'è
  // affatto. Senza questa soglia, un indice fisso su quelle righe
  // prenderebbe per sbaglio il pezzo vicino invece di restare vuoto. Vedi lo
  // stesso concetto in routes/ordini.js estraiValore.
  const [minPartiAttivo, setMinPartiAttivo] = useState(Number.isFinite(Number(estrazioneIniziale?.minParti)));
  const [minParti, setMinParti] = useState(
    Number.isFinite(Number(estrazioneIniziale?.minParti)) ? Number(estrazioneIniziale.minParti) : 2
  );

  // --- Modalità "posizione" (intervallo di caratteri, per stringhe senza separatori) ---
  const [rangeInizio, setRangeInizio] = useState(null); // indice fisico 0-based
  const [rangeFine, setRangeFine] = useState(null);

  // Direzione di conteggio, condivisa da entrambe le modalità.
  const [direzione, setDirezione] = useState(
    estrazioneIniziale && estrazioneIniziale.modalita !== "regex"
      && Number(estrazioneIniziale.modalita === "posizione" ? estrazioneIniziale.da : estrazioneIniziale.indice) >= 0
      ? "inizio" : "fine"
  );

  const valoreEsempio = esempi[indiceEsempio] ?? "";
  const parti = useMemo(() => dividi(valoreEsempio, separatore), [valoreEsempio, separatore]);
  const caratteri = useMemo(() => valoreEsempio.split(""), [valoreEsempio]);

  // Alla primissima apertura, se stiamo modificando un'estrazione "posizione"
  // già esistente, ricostruiamo l'intervallo fisico per la riga di esempio
  // corrente (potrebbe avere una lunghezza diversa da quella con cui è stata
  // creata la regola: usiamo comunque da/a con la stessa convenzione).
  useEffect(() => {
    if (estrazioneIniziale?.modalita === "posizione" && caratteri.length > 0) {
      const len = caratteri.length;
      let da = Number(estrazioneIniziale.da);
      let a = Number(estrazioneIniziale.a);
      if (!Number.isFinite(da)) da = 0;
      if (!Number.isFinite(a)) a = len - 1;
      const s = da < 0 ? Math.max(0, len + da) : Math.min(da, len - 1);
      const e = a < 0 ? len + a : Math.min(a, len - 1);
      setRangeInizio(s);
      setRangeFine(Math.max(s, e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function etichettaPosizione(p) {
    return direzione === "fine" ? -(parti.length - p) : p;
  }

  const fisica = (etichetta) => (direzione === "fine" ? parti.length + etichetta : etichetta);
  const inizioFis = indiceScelto != null ? fisica(indiceScelto) : null;
  const fineFis = indiceSceltoA != null ? fisica(indiceSceltoA) : inizioFis;

  const estrazioneCorrente = indiceScelto != null
    ? {
      separatore, indice: indiceScelto,
      ...(indiceSceltoA != null ? { indiceA: indiceSceltoA } : {}),
      ...(minPartiAttivo && Number.isFinite(minParti) ? { minParti } : {}),
    }
    : null;
  const parteSelezionata = estrazioneCorrente ? anteprimaEstrazione(valoreEsempio, estrazioneCorrente) : null;

  // Estrazione "regola di testo" corrente (null finché non è completa): con i
  // due testi si salva anche "tra", per poterli rimostrare nei campi alla
  // prossima apertura.
  const patternRegex = regolaManuale ? patternManuale : (aLista(traDa).length || aLista(traA).length ? patternTra(traDa, traA) : "");
  const estrazioneRegex = patternRegex !== "" && anteprimaRegex("", patternRegex) !== null
    ? { modalita: "regex", pattern: patternRegex, gruppo: 1, ...(regolaManuale ? {} : { tra: { da: aLista(traDa), a: aLista(traA) } }) }
    : null;
  const risultatoRegex = estrazioneRegex ? anteprimaRegex(valoreEsempio, estrazioneRegex.pattern) : null;

  // Click su una pillola. Modalità normale: sceglie quella sola. Modalità
  // "intervallo": il primo click fissa l'inizio, il secondo la fine (in
  // qualunque ordine); un terzo click ricomincia.
  function cliccaPillola(p) {
    if (!modoIntervallo || indiceScelto == null || indiceSceltoA != null) {
      setIndiceScelto(etichettaPosizione(p));
      setIndiceSceltoA(null);
      return;
    }
    const lo = Math.min(inizioFis, p);
    const hi = Math.max(inizioFis, p);
    setIndiceScelto(etichettaPosizione(lo));
    setIndiceSceltoA(etichettaPosizione(hi));
  }

  function cambiaModoIntervallo(attivo) {
    setModoIntervallo(attivo);
    if (!attivo) setIndiceSceltoA(null);
  }

  const rangeCompleto = rangeInizio != null && rangeFine != null;
  const testoSelezionato = rangeCompleto ? caratteri.slice(rangeInizio, rangeFine + 1).join("") : null;

  // L'indice scelto NON si azzera cambiando separatore: è un numero relativo
  // ("-1" = ultimo pezzo, "0" = primo), che resta valido — e quasi sempre
  // ancora quello giusto — qualunque sia il separatore, visto che il numero
  // di pezzi raramente cambia in modo da invalidarlo. Azzerarlo costringeva
  // a ricliccare la pillola ogni volta, disabilitando "Usa questa parte"
  // anche solo per aver provato un separatore diverso.
  function scegliSeparatoreRapido(v) {
    setSeparatore(v);
    setSeparatorePersonalizzato("");
  }

  function scegliSeparatorePersonalizzato(v) {
    setSeparatorePersonalizzato(v);
    setSeparatore(v);
  }

  function cambiaDirezione(d) {
    setDirezione(d);
    // le pillole/caratteri cliccati restano alla stessa posizione fisica,
    // ricalcolo solo l'etichetta mostrata (la selezione non cambia)
    if (modalita === "separatore" && indiceScelto != null) {
      const converti = (e) => {
        const posizioneFisica = direzione === "fine" ? parti.length + e : e;
        return d === "fine" ? -(parti.length - posizioneFisica) : posizioneFisica;
      };
      setIndiceScelto(converti(indiceScelto));
      if (indiceSceltoA != null) setIndiceSceltoA(converti(indiceSceltoA));
    }
  }

  function cambiaModalita(m) {
    setModalita(m);
    setIndiceScelto(null);
    setIndiceSceltoA(null);
    setRangeInizio(null);
    setRangeFine(null);
  }

  function cliccaCarattere(indiceFisico) {
    if (rangeInizio == null || rangeCompleto) {
      // nessuna selezione in corso, o selezione già completa: si riparte
      setRangeInizio(indiceFisico);
      setRangeFine(null);
      return;
    }
    // completa la selezione (in ordine, qualunque sia il verso del click)
    setRangeInizio(Math.min(rangeInizio, indiceFisico));
    setRangeFine(Math.max(rangeInizio, indiceFisico));
  }

  // Selezione col mouse nella modalità "regola di testo": la parte scelta
  // diventa il risultato voluto, e i due testi di contorno si deducono da
  // quello che le sta subito attorno (la serie di simboli/spazi prima e dopo).
  function selezionaParteConMouse(e) {
    const sel = window.getSelection();
    const nodo = e.currentTarget.firstChild;
    if (!sel || sel.isCollapsed || !nodo || sel.anchorNode !== nodo || sel.focusNode !== nodo) return;
    const t = valoreEsempio;
    let a = Math.min(sel.anchorOffset, sel.focusOffset);
    let b = Math.max(sel.anchorOffset, sel.focusOffset);
    while (a < b && /\s/.test(t[a])) a++;
    while (b > a && /\s/.test(t[b - 1])) b--;
    if (a >= b) return;
    const prima = t.slice(0, a);
    const dopo = t.slice(b);
    setTraDa([a === 0 ? "" : (prima.match(/[^\p{L}\p{N}]+$/u)?.[0] ?? prima.slice(-1))]);
    setTraA([b === t.length ? "" : (dopo.match(/^[^\p{L}\p{N}]+/u)?.[0] ?? dopo.slice(0, 1))]);
    setTestoScelto(t.slice(a, b));
    sel.removeAllRanges();
  }

  function confermaScelta() {
    if (modalita === "regex") {
      if (estrazioneRegex) onConferma(estrazioneRegex);
      return;
    }
    if (modalita === "posizione") {
      if (!rangeCompleto) return;
      const len = caratteri.length;
      const da = fisicaAEtichetta(rangeInizio, len, direzione);
      const a = fisicaAEtichetta(rangeFine, len, direzione);
      onConferma({ modalita: "posizione", da, a });
      return;
    }
    if (indiceScelto == null) return;
    const extra = minPartiAttivo && Number.isFinite(minParti) ? { minParti } : {};
    const intervallo = indiceSceltoA != null ? { indiceA: indiceSceltoA } : {};
    onConferma({ modalita: "separatore", separatore, indice: indiceScelto, ...intervallo, ...extra });
  }

  const puoConfermare = modalita === "regex" ? estrazioneRegex != null
    : modalita === "posizione" ? rangeCompleto : indiceScelto != null;

  return (
    <div
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15, 23, 42, 0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, fontFamily: "Inter, sans-serif" }}
      onClick={onAnnulla}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: "var(--cf-bg-card)", borderRadius: "16px", width: "560px", maxWidth: "92vw", maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 25px 60px rgba(0,0,0,0.45)" }}
      >
        <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)", padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "20px", lineHeight: 1 }}>✂️</span>
            <div>
              <div style={{ fontFamily: "'Roboto Mono', Consolas, monospace", fontWeight: 700, fontSize: "15px", letterSpacing: "1px", textTransform: "uppercase", color: "#f1f5f9" }}>
                Estrai una parte
              </div>
              <div style={{ fontFamily: "'Roboto Mono', Consolas, monospace", fontSize: "11px", color: "var(--cf-text-faint)", letterSpacing: "0.3px", marginTop: "3px" }}>
                {nomeColonnaSorgente} → <span style={{ color: "#22d3ee", fontWeight: 700 }}>{nomeColonnaDestinazione}</span>
              </div>
            </div>
          </div>
          <button className="cf-btn" onClick={onAnnulla} style={{ border: "none", background: "rgba(255,255,255,0.08)", borderRadius: "6px", width: "28px", height: "28px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer", color: "#cbd5e1", lineHeight: 1 }}>
            ✕
          </button>
        </div>

        <div style={{ padding: "24px", overflowY: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--cf-text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", fontFamily: "'Roboto Mono', Consolas, monospace" }}>
              1. Valore reale nel file cliente
            </div>
            {esempi.length > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <button onClick={() => { setIndiceEsempio((i) => Math.max(0, i - 1)); setIndiceScelto(null); setIndiceSceltoA(null); setRangeInizio(null); setRangeFine(null); }} disabled={indiceEsempio === 0} style={{ padding: "2px 8px" }}>◀</button>
                <span style={{ fontSize: "12px", color: "var(--cf-text-muted)" }}>riga {indiceEsempio + 1} / {esempi.length}</span>
                <button onClick={() => { setIndiceEsempio((i) => Math.min(esempi.length - 1, i + 1)); setIndiceScelto(null); setIndiceSceltoA(null); setRangeInizio(null); setRangeFine(null); }} disabled={indiceEsempio === esempi.length - 1} style={{ padding: "2px 8px" }}>▶</button>
              </div>
            )}
          </div>
          <div style={{ padding: "10px 14px", borderRadius: "8px", border: "1px solid var(--cf-border-strong)", backgroundColor: "var(--cf-bg-subtle)", fontSize: "14px", color: "var(--cf-text-primary)", marginBottom: "20px", wordBreak: "break-word" }}>
            {valoreEsempio || <span className="testo-secondario">(vuoto per questa riga)</span>}
          </div>

          <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--cf-text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "8px", fontFamily: "'Roboto Mono', Consolas, monospace" }}>
            2. Come vuoi estrarre?
          </div>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "18px" }}>
            <button onClick={() => cambiaModalita("separatore")} style={modalita === "separatore" ? stileBtnAttivo : undefined}>
              Dividi per separatore
            </button>
            <button onClick={() => cambiaModalita("posizione")} style={modalita === "posizione" ? stileBtnAttivo : undefined}>
              Taglia per posizione (senza separatori)
            </button>
            <button onClick={() => cambiaModalita("regex")} style={modalita === "regex" ? stileBtnAttivo : undefined}>
              Regola di testo (tra due testi)
            </button>
          </div>

          {modalita === "regex" ? (
            <>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--cf-text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "8px", fontFamily: "'Roboto Mono', Consolas, monospace" }}>
                3. Tra quali testi sta la parte che ti serve?
              </div>
              <p className="testo-secondario" style={{ marginTop: 0 }}>
                Utile quando il numero di parole cambia da file a file ma i simboli che stanno intorno alla parte che ti serve
                (es. "&gt;" prima e " - " dopo) sono sempre gli stessi.
              </p>
              {!regolaManuale && (
                <>
                  <p style={{ margin: "0 0 6px", fontWeight: 600 }}>
                    👉 Seleziona con il mouse, qui sotto, la parte che ti serve
                  </p>
                  <div
                    onMouseUp={selezionaParteConMouse}
                    style={{
                      padding: "10px 14px", borderRadius: "8px", border: "2px dashed #1e3a8a", cursor: "text",
                      userSelect: "text", wordBreak: "break-word", marginBottom: "10px",
                      fontFamily: "'Roboto Mono', Consolas, monospace", fontSize: "13px", backgroundColor: "var(--cf-bg-subtle)",
                    }}
                  >{valoreEsempio}</div>
                  {testoScelto != null && risultatoRegex !== testoScelto && (
                    <p className="errore" style={{ marginTop: 0 }}>
                      Con questi testi esce "{risultatoRegex || "niente"}" e non "{testoScelto}": prova ad allungarli
                      (aggiungi qualche carattere in più) o a selezionare di nuovo.
                    </p>
                  )}
                  <p className="testo-secondario" style={{ marginTop: 0 }}>
                    I testi qui sotto si compilano da soli; puoi anche correggerli. <strong>Gli spazi contano</strong>: li vedi come "·".
                  </p>
                </>
              )}
              {!regolaManuale ? (
                <>
                  {[
                    { titolo: "Dopo il testo", vuoto: "(vuoto = dall'inizio)", valori: traDa, imposta: setTraDa, segnaposto: "es. > " },
                    { titolo: "e prima del testo", vuoto: "(vuoto = fino alla fine)", valori: traA, imposta: setTraA, segnaposto: "es.  - " },
                  ].map((campo) => (
                    <div key={campo.titolo} style={{ marginBottom: "8px" }}>
                      {campo.valori.map((v, i) => (
                        <div key={i} className="riga-controlli" style={{ marginBottom: "4px" }}>
                          <label style={{ minWidth: "8.5rem" }}>{i === 0 ? campo.titolo : "oppure"}</label>
                          <input
                            type="text"
                            value={v}
                            placeholder={campo.segnaposto}
                            onChange={(e) => campo.imposta(campo.valori.map((x, k) => (k === i ? e.target.value : x)))}
                            style={{ width: "10rem", fontFamily: "'Roboto Mono', Consolas, monospace" }}
                          />
                          {campo.valori.length > 1 ? (
                            <button type="button" title="Togli questa alternativa" onClick={() => campo.imposta(campo.valori.filter((_, k) => k !== i))} style={{ padding: "2px 8px" }}>✕</button>
                          ) : (
                            <span className="testo-secondario">{campo.vuoto}</span>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => campo.imposta([...campo.valori, ""])}
                        title="Se in alcuni file il simbolo è diverso, aggiungi qui anche l'altro: vale quello che trova"
                      >
                        + aggiungi un'alternativa
                      </button>
                    </div>
                  ))}
                  {(aLista(traDa).length > 0 || aLista(traA).length > 0) && (
                    <p className="testo-secondario" style={{ marginTop: 0 }}>
                      Cerco: dopo {aLista(traDa).length ? aLista(traDa).map((t) => `"${visibile(t)}"`).join(" oppure ") : "(inizio)"}
                      {" "}e prima di {aLista(traA).length ? aLista(traA).map((t) => `"${visibile(t)}"`).join(" oppure ") : "(fine)"}
                    </p>
                  )}
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => { setPatternManuale(patternTra(traDa, traA)); setRegolaManuale(true); }}
                    title="Per casi che i due testi non bastano a descrivere: scrivi tu l'espressione regolare"
                  >
                    Scrivi la regola a mano (avanzato)
                  </button>
                </>
              ) : (
                <>
                  <div className="riga-controlli" style={{ marginBottom: "8px" }}>
                    <label>Espressione regolare</label>
                    <input
                      type="text"
                      value={patternManuale}
                      onChange={(e) => setPatternManuale(e.target.value)}
                      style={{ width: "100%", fontFamily: "'Roboto Mono', Consolas, monospace" }}
                    />
                  </div>
                  <p className="testo-secondario" style={{ marginTop: 0 }}>
                    Si usa quello che sta nel <strong>primo gruppo tra parentesi</strong>. Se la regola non trova niente
                    il campo resta vuoto.
                  </p>
                  <button type="button" className="btn-link" onClick={() => setRegolaManuale(false)}>
                    Torna ai due testi
                  </button>
                </>
              )}

              {estrazioneRegex == null && patternRegex !== "" && (
                <p className="errore">La regola non è valida: controlla le parentesi e i caratteri speciali.</p>
              )}
              {estrazioneRegex && (
                <div style={{ padding: "12px 14px", borderRadius: "8px", backgroundColor: "var(--cf-bg-subtle)", border: "1px solid var(--cf-border)", margin: "10px 0 6px" }}>
                  <span style={{ fontSize: "13px", color: "var(--cf-text-muted)" }}>Verrà usato: </span>
                  <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--cf-text-primary)" }}>
                    {risultatoRegex ? `"${risultatoRegex}"` : "(niente: la regola non trova nulla su questa riga)"}
                  </span>
                  {risultatoRegex && !regolaManuale && (() => {
                    const t = testiTrovati(valoreEsempio, traDa, traA);
                    return t ? (
                      <div style={{ fontSize: "12px", color: "var(--cf-text-muted)", marginTop: "4px" }}>
                        trovato tra "{visibile(t.da) || "inizio"}" e "{visibile(t.a) || "fine"}"
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
              {estrazioneRegex && esempi.length > 1 && (
                <div style={{ marginTop: "10px", fontSize: "12px", color: "var(--cf-text-muted)" }}>
                  <div style={{ marginBottom: "4px" }}>Controllo sulle altre righe:</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "2px 8px", maxHeight: "9rem", overflowY: "auto" }}>
                    {esempi.slice(0, 12).map((e, k) => (
                      <React.Fragment key={k}>
                        <span style={{ wordBreak: "break-word" }}>{e}</span>
                        <span>→</span>
                        <strong style={{ color: "var(--cf-text-primary)", wordBreak: "break-word" }}>
                          {anteprimaRegex(e, estrazioneRegex.pattern) || "—"}
                          {!regolaManuale && anteprimaRegex(e, estrazioneRegex.pattern) && (() => {
                            const t = testiTrovati(e, traDa, traA);
                            return t ? <span style={{ fontWeight: 400, color: "var(--cf-text-muted)" }}> (tra "{visibile(t.da) || "inizio"}" e "{visibile(t.a) || "fine"}")</span> : null;
                          })()}
                        </strong>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : modalita === "separatore" ? (
            <>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--cf-text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "8px", fontFamily: "'Roboto Mono', Consolas, monospace" }}>
                3. Dove spezzare il testo
              </div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "20px" }}>
                {SEPARATORI_RAPIDI.map((s) => (
                  <button
                    key={s.valore}
                    onClick={() => scegliSeparatoreRapido(s.valore)}
                    style={separatore === s.valore && !separatorePersonalizzato ? stileBtnAttivo : undefined}
                  >
                    {s.etichetta}
                  </button>
                ))}
                <input
                  type="text"
                  placeholder="altro carattere..."
                  value={separatorePersonalizzato}
                  onChange={(e) => scegliSeparatorePersonalizzato(e.target.value)}
                  style={{ width: "8rem" }}
                />
              </div>

              <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--cf-text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "8px", fontFamily: "'Roboto Mono', Consolas, monospace" }}>
                4. Clicca la parte che ti serve
              </div>
              <div className="riga-controlli" style={{ marginBottom: "10px" }}>
                <span style={{ fontSize: "12px", color: "var(--cf-text-muted)" }}>Se il numero di parole cambia da riga a riga, conta:</span>
                <button onClick={() => cambiaDirezione("inizio")} style={direzione === "inizio" ? stileBtnAttivo : undefined}>Dall'inizio</button>
                <button onClick={() => cambiaDirezione("fine")} style={direzione === "fine" ? stileBtnAttivo : undefined}>Dalla fine (consigliato)</button>
              </div>

              <label className="riga-controlli" style={{ marginBottom: "10px", fontSize: "12px", color: "var(--cf-text-muted)" }}>
                <input type="checkbox" checked={modoIntervallo} onChange={(e) => cambiaModoIntervallo(e.target.checked)} />
                Mi servono più parole di fila: clicca la prima e poi l'ultima
              </label>

              {parti.length === 0 ? (
                <p className="errore">Il valore di questa riga è vuoto: prova un'altra riga di esempio con la freccia sopra.</p>
              ) : (
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "18px" }}>
                  {parti.map((parte, p) => {
                    const etichetta = etichettaPosizione(p);
                    const selezionata = inizioFis != null && p >= inizioFis && p <= fineFis;
                    return (
                      <button
                        key={p}
                        onClick={() => cliccaPillola(p)}
                        title={`posizione ${etichetta}`}
                        style={{
                          display: "flex", flexDirection: "column", alignItems: "center", gap: "2px",
                          padding: "8px 14px", borderRadius: "10px",
                          border: selezionata ? "2px solid #1e3a8a" : "1px solid var(--cf-border-strong)",
                          backgroundColor: selezionata ? "#1e3a8a" : "var(--cf-bg-subtle)",
                          color: selezionata ? "#fff" : "var(--cf-text-primary)",
                          fontWeight: selezionata ? 700 : 500,
                        }}
                      >
                        <span>{parte}</span>
                        <span style={{ fontSize: "10px", opacity: 0.7 }}>{etichetta}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {parteSelezionata != null && (
                <div style={{ padding: "12px 14px", borderRadius: "8px", backgroundColor: "var(--cf-bg-subtle)", border: "1px solid var(--cf-border)", marginBottom: "6px" }}>
                  <span style={{ fontSize: "13px", color: "var(--cf-text-muted)" }}>Verrà usato: </span>
                  <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--cf-text-primary)" }}>"{parteSelezionata}"</span>
                </div>
              )}

              {estrazioneCorrente && esempi.length > 1 && (
                <div style={{ marginTop: "10px", fontSize: "12px", color: "var(--cf-text-muted)" }}>
                  <div style={{ marginBottom: "4px" }}>Controllo sulle altre righe:</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "2px 8px", maxHeight: "9rem", overflowY: "auto" }}>
                    {esempi.slice(0, 12).map((e, k) => (
                      <React.Fragment key={k}>
                        <span style={{ wordBreak: "break-word" }}>{e}</span>
                        <span>→</span>
                        <strong style={{ color: "var(--cf-text-primary)", wordBreak: "break-word" }}>{anteprimaEstrazione(e, estrazioneCorrente) || "—"}</strong>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}

              <label
                className="riga-controlli"
                style={{ marginTop: "10px", fontSize: "12px", color: "var(--cf-text-muted)" }}
                title="Per quando un pezzo IN MEZZO (non il primo né l'ultimo) a volte manca del tutto invece di essere solo più corto: senza questa soglia, su quelle righe l'indice scelto prenderebbe per sbaglio il pezzo vicino invece di restare vuoto."
              >
                <input type="checkbox" checked={minPartiAttivo} onChange={(e) => setMinPartiAttivo(e.target.checked)} />
                Lascia vuoto se questa riga ha meno di
                <input
                  type="number"
                  min="1"
                  disabled={!minPartiAttivo}
                  value={minParti}
                  onChange={(e) => setMinParti(Number(e.target.value) || 1)}
                  style={{ width: "3.5rem" }}
                />
                pezzi in tutto (righe più corte per un pezzo opzionale mancante, non solo diviso diversamente)
              </label>
            </>
          ) : (
            <>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--cf-text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "8px", fontFamily: "'Roboto Mono', Consolas, monospace" }}>
                3. Clicca il primo e l'ultimo carattere che ti servono
              </div>
              <div className="riga-controlli" style={{ marginBottom: "10px" }}>
                <span style={{ fontSize: "12px", color: "var(--cf-text-muted)" }}>Se la lunghezza del codice cambia da riga a riga, conta:</span>
                <button onClick={() => cambiaDirezione("inizio")} style={direzione === "inizio" ? stileBtnAttivo : undefined}>Dall'inizio</button>
                <button onClick={() => cambiaDirezione("fine")} style={direzione === "fine" ? stileBtnAttivo : undefined}>Dalla fine (consigliato)</button>
              </div>

              {caratteri.length === 0 ? (
                <p className="errore">Il valore di questa riga è vuoto: prova un'altra riga di esempio con la freccia sopra.</p>
              ) : (
                <div style={{ display: "flex", gap: "3px", flexWrap: "wrap", marginBottom: "18px" }}>
                  {caratteri.map((car, p) => {
                    const etichetta = fisicaAEtichetta(p, caratteri.length, direzione);
                    const dentroRange = rangeInizio != null && p >= rangeInizio && p <= (rangeFine ?? rangeInizio);
                    const bordoRange = p === rangeInizio || p === rangeFine;
                    return (
                      <button
                        key={p}
                        onClick={() => cliccaCarattere(p)}
                        title={`posizione ${etichetta}`}
                        style={{
                          display: "flex", flexDirection: "column", alignItems: "center", gap: "1px",
                          padding: "6px 9px", borderRadius: "6px", minWidth: "1.6rem",
                          border: bordoRange ? "2px solid #1e3a8a" : dentroRange ? "1px solid #1e3a8a" : "1px solid var(--cf-border-strong)",
                          backgroundColor: dentroRange ? "#1e3a8a" : "var(--cf-bg-subtle)",
                          color: dentroRange ? "#fff" : "var(--cf-text-primary)",
                          fontWeight: dentroRange ? 700 : 500,
                          fontFamily: "'Roboto Mono', Consolas, monospace",
                        }}
                      >
                        <span>{car === " " ? " " : car}</span>
                        <span style={{ fontSize: "9px", opacity: 0.7 }}>{etichetta}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="testo-secondario" style={{ marginTop: 0, marginBottom: "12px" }}>
                {rangeInizio == null
                  ? "Clicca il primo carattere della parte che ti serve."
                  : !rangeCompleto
                    ? "Ora clicca l'ultimo carattere (o lo stesso, se te ne serve solo uno)."
                    : "Selezione completa — clicca un altro carattere per ricominciare."}
              </p>

              {testoSelezionato != null && (
                <div style={{ padding: "12px 14px", borderRadius: "8px", backgroundColor: "var(--cf-bg-subtle)", border: "1px solid var(--cf-border)", marginBottom: "6px" }}>
                  <span style={{ fontSize: "13px", color: "var(--cf-text-muted)" }}>Verrà usato: </span>
                  <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--cf-text-primary)" }}>"{testoSelezionato}"</span>
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: "16px 24px", borderTop: "1px solid var(--cf-border)", display: "flex", justifyContent: "flex-end", gap: "10px", flexShrink: 0 }}>
          <button onClick={onAnnulla}>Annulla</button>
          <button className="btn-primario" onClick={confermaScelta} disabled={!puoConfermare}>
            Usa questa parte
          </button>
        </div>
      </div>
    </div>
  );
}
